import React, { useMemo, useRef, useState } from "react";
import {
  TextField,
  ThemeProvider,
  createTheme,
  styled,
  Autocomplete,
} from "@mui/material";
import { Dictionary } from "../../types/Dictionary";
import { DictionaryCombo } from "../../types/DictionaryCombo";
import { toTitleCase, capitalize } from "../../helpers/string";
import { Calendar } from "../Calendar/Calendar";
import { IBeneficiaryItem } from "../../types/IBeneficiaryItem";
import InputMask from "react-input-mask";
import dayjs from "dayjs";
import { TextMaskCustom } from "../MaskComponent/TextMaskCustom";
import { getBirthDateFromPesel } from "../../helpers/GetFirthDateFromPesel";
import { plPL } from "@mui/x-date-pickers/locales";
import { sanitizeValue } from "../../helpers/inputSanitizer";

const TextFieldStyled = styled(TextField)(({ theme }) => ({
  width: "100%",
  "& .MuiOutlinedInput-root .MuiAutocomplete-input:first-child": {
    height: 35,
  },
}));

const defaultMaterialTheme = createTheme({
  palette: {
    primary: {
      main: "#000000",
    },
  },
  components: {
    MuiInputLabel: {
      styleOverrides: {
        root: {
          "&.MuiInputLabel-marginDense": {
            transform: "translate(14px, 20px) scale(1)",
          },
          "&.MuiInputLabel-shrink": {
            transform: "translate(14px, -6px) scale(0.75) !important",
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          display: "none",
        },
      },
    },
  },
});

interface IBeneficiaryItemProps {
  dictionary?: Dictionary;
  errors: string[];
  setErrors: React.Dispatch<React.SetStateAction<string[] | undefined>>;
  item?: IBeneficiaryItem;
  setItem?: React.Dispatch<React.SetStateAction<IBeneficiaryItem | undefined>>;
  setShow?: React.Dispatch<React.SetStateAction<boolean | undefined>>;
  show: boolean;
  hide?: React.Dispatch<React.SetStateAction<boolean | undefined>>;
  number?: number;
  deleteItem: () => void;
}

const BeneficiaryItem = ({
  dictionary,
  errors,
  setErrors,
  item,
  setItem,
  setShow,
  show,
  hide,
  number,
  deleteItem,
}: IBeneficiaryItemProps) => {
  //const { height, width } = useWindowDimensions();

  const [name, setName] = useState<string | undefined>(item?.name);
  const [firstName, setFirstName] = useState<string | undefined>(
    item?.firstName
  );
  const [lastName, setLastName] = useState<string | undefined>(item?.lastName);
  const [citizenship, setCitizenship] = useState<DictionaryCombo | null>(
    item?.citizenship ?? null
  );
  const [pesel, setPesel] = useState<string | undefined>(item?.pesel);
  const [birthDate, setBirthDate] = useState<string | undefined>(
    item?.birthDate
  );
  const [placeOfBirth, setPlaceOfBirth] = useState<string | undefined>(
    item?.placeOfBirth
  );
  const [countryOfBirth, setCountryOfBirth] = useState<DictionaryCombo | null>(
    item?.countryOfBirth ?? null
  );
  const [documentType, setDocumentType] = useState<DictionaryCombo | null>(
    item?.documentType ?? null
  );
  const [seriesAndNumber, setSeriesAndNumber] = useState<string | undefined>(
    item?.seriesAndNumber
  );
  const [issuingCountry, setIssuingCountry] = useState<DictionaryCombo | null>(
    item?.issuingCountry ?? null
  );
  const [country, setCountry] = useState<DictionaryCombo | null>(
    item?.country ?? null
  );
  const [postCode, setPostCode] = useState<string | undefined>(item?.postCode);
  const [city, setCity] = useState<string | undefined>(item?.city);
  const [street, setStreet] = useState<string | undefined>(item?.street);
  const [house, setHouse] = useState<string | undefined>(item?.house);
  const [apartment, setApartment] = useState<string | undefined>(
    item?.apartment
  );
  const [option, setOption] = useState<number | undefined>(item?.option);
  const [pppText, setPppText] = useState<string | undefined>(item?.pppText);

  const [firstNameError, setFirstNameError] = useState<boolean>(false);
  const [firstLastNameError, setFirstLastNameError] = useState<boolean>(false);
  const [optionError, setOptionError] = useState<boolean>(false);
  const [optionNameError, setOptionNameError] = useState<boolean>(false);
  const [postCodeError, setPostCodeError] = useState<boolean>(false);
  const [seriesAndNumberError, setSeriesAndNumberError] =
    useState<boolean>(false);
  const [checkAddressPostalCode, setCheckAddressPostalCode] =
    useState<boolean>(false);
  const [checkFirstName, setCheckFirstName] = useState<boolean>(false);
  const [checkLastName, setCheckLastName] = useState<boolean>(false);
  const [checkOption, setCheckOption] = useState<boolean>(false);
  const [checkSeriesAndNumber, setCheckSeriesAndNumber] =
    useState<boolean>(false);
  const [showBirthDateError, setShowBirthDateError] = useState<boolean>(false);
  const [showPeselError, setShowPeselError] = useState<boolean>(false);
  const [issuingCountryRequiredError, setIssuingCountryRequiredError] =
    useState<boolean>(false);
  const [seriesAndNumberRequiredError, setSeriesAndNumberRequiredError] =
    useState<boolean>(false);

  const [firstNameInvalidChars, setFirstNameInvalidChars] = useState<
    string | null
  >(null);
  const [lastNameInvalidChars, setLastNameInvalidChars] = useState<
    string | null
  >(null);
  const [placeOfBirthInvalidChars, setPlaceOfBirthInvalidChars] = useState<
    string | null
  >(null);
  const [cityInvalidChars, setCityInvalidChars] = useState<string | null>(null);
  const [streetInvalidChars, setStreetInvalidChars] = useState<string | null>(
    null
  );
  const [pppTextInvalidChars, setPppTextInvalidChars] = useState<string | null>(
    null
  );

  const invalidCharTimeoutsRef = useRef<
    Record<string, ReturnType<typeof setTimeout> | null>
  >({});

  const getModel = () => {
    return {
      name,
      firstName,
      lastName,
      citizenship,
      pesel,
      birthDate,
      placeOfBirth,
      countryOfBirth,
      documentType,
      seriesAndNumber,
      issuingCountry,
      country,
      postCode,
      city,
      street,
      house,
      apartment,
      option,
      pppText,
    };
  };

  useMemo(() => {
    setName(item?.name);
    setFirstName(item?.firstName);
    setLastName(item?.lastName);
    setCitizenship(item?.citizenship ?? null);
    setPesel(item?.pesel);
    setBirthDate(item?.birthDate);
    setPlaceOfBirth(item?.placeOfBirth);
    setCountryOfBirth(item?.countryOfBirth ?? null);
    setDocumentType(item?.documentType ?? null);
    setSeriesAndNumber(item?.seriesAndNumber);
    setIssuingCountry(item?.issuingCountry ?? null);
    setCountry(item?.country ?? null);
    setPostCode(item?.postCode);
    setCity(item?.city);
    setStreet(item?.street);
    setHouse(item?.house);
    setApartment(item?.apartment);
    setOption(item?.option);
    setPppText(item?.pppText);
  }, [item]);

  const compareDictionary = (a: DictionaryCombo, b: DictionaryCombo) => {
    if (a.name < b.name) {
      return -1;
    }
    if (a.name > b.name) {
      return 1;
    }
    return 0;
  };

  const polandFirst = (acc: DictionaryCombo[], element: DictionaryCombo) => {
    if (element.name?.toLocaleLowerCase() === "polska") {
      return [element, ...acc];
    }
    return [...acc, element];
  };

  const checkPesel = (pesel: string) => {
    // 1. Sprawdzenie długości i wyłącznie cyfr
    if (!/^\d{11}$/.test(pesel)) {
      return false;
    }

    // 2. Sprawdzanie miesiąca (2-3 cyfra) i dnia (4-5 cyfra)
    const monthWithCentury = Number(pesel.substring(2, 4));
    if (!monthWithCentury || monthWithCentury % 20 > 12) {
      return false;
    }
    const day = Number(pesel.substring(4, 6));
    if (day < 1 || day > 31) {
      return false;
    }

    // 3. Walidacja sumy kontrolnej PESEL
    const weights = [1, 3, 7, 9]; // wagi cyklicznie powtarzane
    const digits = pesel.split("").map((digit) => parseInt(digit, 10));
    const controlDigit = digits[10];
    digits.pop(); // usuwa ostatnią cyfrę (kontrolną)

    const controlSum =
      digits.reduce((acc, curr, idx) => {
        return acc + curr * weights[idx % 4];
      }, 0) % 10;

    const isChecksumValid =
      10 - (controlSum === 0 ? 10 : controlSum) === controlDigit;
    if (!isChecksumValid) {
      return false;
    }

    // 4. Wyliczenie faktycznej daty urodzenia (dayjs)
    const birthDateStr = getBirthDateFromPesel(pesel);
    // zakładamy, że getBirthDateFromPesel zwraca np. "YYYY-MM-DD"
    const birthDate = dayjs(birthDateStr);

    // 5. Sprawdzenie, czy osoba ma >= 18 lat
    // Porównujemy różnicę w latach z dokładnością (opcjonalnie 'year', 'month', 'day' - w tym przypadku 'year' wystarczy).
    if (dayjs().diff(birthDate, "year", true) < 18) {
      return false;
    }

    // 6. (Opcjonalne) sprawdzenie, czy nie jest starsza niż 105 lat
    if (dayjs().diff(birthDate, "year", true) > 105) {
      return false;
    }

    // 7. Jeśli wszystko jest ok, ustawiamy stan w komponencie i zwracamy true
    setBirthDate(birthDateStr);
    return true;
  };

  const isEmpty = (text?: string) => {
    if (text == null || text === "") return true;
    return false;
  };

  const validateDO = (numer: string | undefined) => {
    if (numer == null || numer.length != 9) return false;

    numer = numer.toUpperCase();
    const letterValues = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ";

    for (let i = 0; i < 3; i++)
      if (
        letterValues.indexOf(numer[i]) < 10 ||
        numer[i] == "O" ||
        numer == "Q"
      )
        return false;

    for (let i = 3; i < 9; i++)
      if (
        letterValues.indexOf(numer[i]) < 0 ||
        letterValues.indexOf(numer[i]) > 9
      )
        return false;

    let sum =
      7 * letterValues.indexOf(numer[0]) +
      3 * letterValues.indexOf(numer[1]) +
      1 * letterValues.indexOf(numer[2]) +
      7 * letterValues.indexOf(numer[4]) +
      3 * letterValues.indexOf(numer[5]) +
      1 * letterValues.indexOf(numer[6]) +
      7 * letterValues.indexOf(numer[7]) +
      3 * letterValues.indexOf(numer[8]);
    sum %= 10;
    if (sum != letterValues.indexOf(numer[3])) return false;

    return true;
  };

  const validatePassport = (number: string) => {
    const tab: number[] = [7, 3, 9, 1, 7, 3, 1, 7, 3];

    number = number.trim().replace(/\s+/g, "");

    if (number.length !== 9) {
      return false;
    }

    let charCode: number;

    for (let i = 0; i < 2; i++) {
      charCode = number.charCodeAt(i);
      if (charCode < 65 || charCode > 90) {
        return false;
      }
    }

    for (let i = 2; i < 9; i++) {
      charCode = number.charCodeAt(i);
      if (charCode < 48 || charCode > 57) {
        return false;
      }
    }

    let sum = 0;

    for (let i = 0; i < 9; i++) {
      if (i < 2) {
        // Dla liter: A=10, B=11, ..., Z=35
        sum += (number.charCodeAt(i) - 55) * tab[i];
      } else {
        sum += parseInt(number[i], 10) * tab[i];
      }
    }

    return sum % 10 === 0;
  };

  const setFirstNameErrorHandler = (value: string | undefined) => {
    if (isEmpty(value)) {
      setFirstNameError(true);
    } else {
      setFirstNameError(false);
    }
  };

  const setFirstLastNameErrorHandler = (value: string | undefined) => {
    if (isEmpty(value)) {
      setFirstLastNameError(true);
    } else {
      setFirstLastNameError(false);
    }
  };

  const setSeriesAndNumberErrorHandler = (
    country: DictionaryCombo | null,
    documentType: DictionaryCombo | null,
    seriesAndNumber: string | undefined
  ) => {
    if (!isEmpty(documentType?.name)) {
      if (isEmpty(country?.name)) {
        setIssuingCountryRequiredError(true);
      } else {
        setIssuingCountryRequiredError(false);
      }

      if (isEmpty(seriesAndNumber)) {
        setSeriesAndNumberRequiredError(true);
      } else {
        setSeriesAndNumberRequiredError(false);
      }
    } else {
      setIssuingCountryRequiredError(false);
      setSeriesAndNumberRequiredError(false);
    }

    if (
      isEmpty(seriesAndNumber) ||
      country?.name?.toLocaleLowerCase() !== "polska"
    ) {
      setSeriesAndNumberError(false);
      return;
    }
    switch (documentType?.name?.toLocaleLowerCase()) {
      case "dowód osobisty":
        if (
          seriesAndNumber?.match(
            new RegExp(/\b[A-Z][A-Z][A-Z][0-9][0-9][0-9][0-9][0-9][0-9]\b/)
          ) == null ||
          validateDO(seriesAndNumber) !== true
        ) {
          setSeriesAndNumberError(true);
        } else {
          setSeriesAndNumberError(false);
        }
        break;
      case "paszport":
        if (
          seriesAndNumber?.match(
            new RegExp(/\b[A-Z][A-Z][0-9][0-9][0-9][0-9][0-9][0-9][0-9]\b/)
          ) == null ||
          validatePassport(seriesAndNumber) !== true
        ) {
          setSeriesAndNumberError(true);
        } else {
          setSeriesAndNumberError(false);
        }
        break;
      case "mdowód":
        if (
          seriesAndNumber?.match(
            new RegExp(/\bM[A-Z][A-Z][A-Z][0-9][0-9][0-9][0-9][0-9]\b/)
          ) == null
        ) {
          setSeriesAndNumberError(true);
        } else {
          setSeriesAndNumberError(false);
        }
        break;
    }
  };

  const setOptionErrorHandler = (option?: number, pppText?: string) => {
    if (option == null) {
      setOptionError(true);
    } else {
      if (number == null && errors.indexOf("firstOption") !== -1) {
        setErrors(errors.filter((item) => item !== "firstOption"));
      }
      if (number === 2 && errors.indexOf("secondOption") !== -1) {
        setErrors(errors.filter((item) => item !== "secondOption"));
      }
      if (number === 3 && errors.indexOf("thirdOption") !== -1) {
        setErrors(errors.filter((item) => item !== "thirdOption"));
      }
      setOptionError(false);
    }
    if (isEmpty(pppText)) {
      setOptionNameError(true);
    } else {
      if (number == null && errors.indexOf("firstOptionName") !== -1) {
        setErrors(errors.filter((item) => item !== "firstOptionName"));
      }
      if (number === 2 && errors.indexOf("secondOptionName") !== -1) {
        setErrors(errors.filter((item) => item !== "secondOptionName"));
      }
      if (number === 3 && errors.indexOf("thirdOptionName") !== -1) {
        setErrors(errors.filter((item) => item !== "thirdOptionName"));
      }
      setOptionNameError(false);
    }
  };

  const getNumber = () => {
    switch (number) {
      case 3:
        return "third";
      case 2:
        return "second";
      case 1:
      default:
        return "first";
    }
  };

  useMemo(() => {
    if (number == null) {
      if (errors.indexOf("firstFirstName") !== -1) {
        setFirstNameError(true);
      }
      if (errors.indexOf("firstLastName") !== -1) {
        setFirstLastNameError(true);
      }
      if (errors.indexOf("firstOptionName") !== -1) {
        setOptionNameError(true);
      }
      if (errors.indexOf("firstOption") !== -1) {
        setOptionError(true);
      }
    } else if (number === 2) {
      if (errors.indexOf("secondFirstName") !== -1) {
        setFirstNameError(true);
      }
      if (errors.indexOf("secondLastName") !== -1) {
        setFirstLastNameError(true);
      }
      if (errors.indexOf("secondOptionName") !== -1) {
        setOptionNameError(true);
      }
      if (errors.indexOf("secondOption") !== -1) {
        setOptionError(true);
      }
    } else if (number === 3) {
      if (errors.indexOf("thirdFirstName") !== -1) {
        setFirstNameError(true);
      }
      if (errors.indexOf("thirdLastName") !== -1) {
        setFirstLastNameError(true);
      }
      if (errors.indexOf("thirdOptionName") !== -1) {
        setOptionNameError(true);
      }
      if (errors.indexOf("thirdOption") !== -1) {
        setOptionError(true);
      }
    }
  }, [errors]);

  useMemo(() => {
    if (setItem) {
      setItem(getModel());
    }
    if (checkFirstName) {
      setFirstNameErrorHandler(firstName);
    }
    if (checkLastName) {
      setFirstLastNameErrorHandler(lastName);
    }

    if (checkOption) {
      setOptionErrorHandler(option, pppText);
    }

    setSeriesAndNumberErrorHandler(
      issuingCountry,
      documentType,
      seriesAndNumber
    );

    if (checkAddressPostalCode && postCode && postCode.length > 0) {
      if (!postCode?.match(new RegExp(/\b[0-9][0-9]-[0-9][0-9][0-9]\b/))) {
        setPostCodeError(true);
      } else {
        setPostCodeError(false);
      }
    } else {
      setPostCodeError(false);
    }
  }, [
    firstName,
    lastName,
    citizenship,
    pesel,
    birthDate,
    placeOfBirth,
    countryOfBirth,
    documentType,
    seriesAndNumber,
    issuingCountry,
    country,
    postCode,
    city,
    street,
    house,
    apartment,
    option,
    pppText,
    checkFirstName,
    checkLastName,
  ]);

  useMemo(() => {
    if (
      isEmpty(birthDate) === false &&
      !birthDate?.match(
        new RegExp(/\b[0-9][0-9][0-9][0-9]-[0-9][0-9]-[0-9][0-9]\b/)
      )
    ) {
      setShowBirthDateError(true);
    } else {
      setShowBirthDateError(false);
    }
  }, [birthDate]);

  useMemo(() => {
    if (pesel && isEmpty(pesel) === false && checkPesel(pesel) === false) {
      setShowPeselError(true);
      setBirthDate(undefined);
    } else {
      setShowPeselError(false);
    }
  }, [pesel]);

  const handleSanitizedChange = (
    rawValue: string,
    setValue: (v: string | undefined) => void,
    fieldKey: string,
    setInvalidMessage: (msg: string | null) => void,
    extra?: (cleaned: string | undefined) => void
  ) => {
    const { cleaned, invalidChars } = sanitizeValue(rawValue);

    if (invalidChars.length > 0) {
      const unique = Array.from(new Set(invalidChars));
      const msg = `Podano nieprawidłowe znaki: ${unique.join(" ")}`;
      setInvalidMessage(msg);

      if (invalidCharTimeoutsRef.current[fieldKey]) {
        clearTimeout(invalidCharTimeoutsRef.current[fieldKey]!);
      }
      invalidCharTimeoutsRef.current[fieldKey] = setTimeout(() => {
        setInvalidMessage(null);
      }, 5000);
    }

    setValue(cleaned === "" ? undefined : cleaned);
    if (extra) extra(cleaned === "" ? undefined : cleaned);
  };

  return (
    <div className="benefit-container">
      <ThemeProvider theme={defaultMaterialTheme}>
        {number ? (
          <>
            <hr className="hrMargin" />

            <p className="bold red section">
              Beneficjent rzeczywisty {number}
              {hide ? (
                <button
                  onClick={() => {
                    if (deleteItem) {
                      deleteItem();
                    }
                  }}
                  className="deleteBtn"
                >
                  Usuń
                </button>
              ) : null}
            </p>
          </>
        ) : null}
        <div className="row px-3 mt-4">
          <div className="form-group mt-1 mb-1">
            <TextFieldStyled
              label="Imię"
              name={`${getNumber()}FirstName`}
              variant="outlined"
              value={firstName}
              error={firstNameError}
              onChange={(event: any) => {
                handleSanitizedChange(
                  event.target.value,
                  setFirstName,
                  "firstName",
                  setFirstNameInvalidChars,
                  (val) => setFirstNameErrorHandler(val)
                );
              }}
              onBlur={(event: any) => {
                if (event.target.value)
                  setFirstName(toTitleCase(event.target.value));
                setCheckFirstName(true);
              }}
              inputProps={{
                maxLength: 50,
              }}
              InputProps={{
                style: { height: 50 },
              }}
            />
            {firstNameError ? (
              <span className="validationError" role="alert">
                <i className="fa fa-exclamation-circle" />
                To pole jest wymagane
              </span>
            ) : null}
            {firstNameInvalidChars && (
              <span className="validationError" role="alert">
                <i className="fa fa-exclamation-circle" />
                {firstNameInvalidChars}
              </span>
            )}
          </div>
        </div>

        <div className="row px-3 mt-4">
          <div className="form-group mt-1 mb-1">
            <TextFieldStyled
              label="Nazwisko"
              name={`${getNumber()}LastName`}
              variant="outlined"
              value={lastName}
              error={firstLastNameError}
              onChange={(event: any) => {
                handleSanitizedChange(
                  event.target.value,
                  setLastName,
                  "lastName",
                  setLastNameInvalidChars,
                  (val) => setFirstLastNameErrorHandler(val)
                );
              }}
              onBlur={(event: any) => {
                if (event.target.value)
                  setLastName(toTitleCase(event.target.value));
                setCheckLastName(true);
              }}
              inputProps={{
                maxLength: 200,
              }}
              InputProps={{
                style: { height: 50 },
              }}
            />
            {firstLastNameError ? (
              <span className="validationError" role="alert">
                <i className="fa fa-exclamation-circle" />
                To pole jest wymagane
              </span>
            ) : null}
            {lastNameInvalidChars && (
              <span className="validationError" role="alert">
                <i className="fa fa-exclamation-circle" />
                {lastNameInvalidChars}
              </span>
            )}
          </div>
        </div>

        <div className="row px-3 mt-4">
          <div className="form-group mt-1 mb-1">
            <Autocomplete
              options={
                dictionary?.citizenships
                  .sort(compareDictionary)
                  .reduce(polandFirst, []) ?? []
              }
              value={citizenship}
              onChange={(event: any, newValue: DictionaryCombo | null) => {
                setCitizenship(newValue);
              }}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              style={{ height: 50 }}
              renderInput={(params) => (
                <TextFieldStyled
                  {...params}
                  label="Kraj obywatelstwa"
                  variant="outlined"
                  size="small"
                />
              )}
              noOptionsText={"Brak danych"}
            />
          </div>
        </div>

        <div className="row px-3 mt-4">
          <div className="form-group mt-1 mb-1">
            <TextFieldStyled
              label="PESEL"
              name="pesel"
              variant="outlined"
              value={pesel}
              onChange={(event: any) => {
                setPesel(event.target.value);
              }}
              inputProps={{
                maxLength: 11,
              }}
              InputProps={{
                style: { height: 50 },
              }}
            />
            {showPeselError && (
              <span className="validationError" role="alert">
                <i className="fa fa-exclamation-circle" />
                Nieprawidłowy PESEL
              </span>
            )}
          </div>
        </div>

        <div className="row px-3 mt-4">
          <div className="form-group mt-1 mb-1">
            <Calendar
              localeText={
                plPL.components.MuiLocalizationProvider.defaultProps.localeText
              }
              disableFuture
              maxDate={dayjs().add(-18, "years")}
              minDate={dayjs().add(-105, "years")}
              format="YYYY-MM-DD"
              label="Data urodzenia"
              value={birthDate ? dayjs(birthDate) : null}
              onChange={(date: any) => {
                setBirthDate(
                  date ? dayjs(date).format("YYYY-MM-DD") : undefined
                );
              }}
              renderInput={(props: any) => (
                <TextFieldStyled
                  {...props}
                  name={`${getNumber()}BirthName`}
                  fullWidth
                  variant="outlined"
                />
              )}
            />
            {showBirthDateError && (
              <span className="validationError" role="alert">
                <i className="fa fa-exclamation-circle" />
                Podano błędną datę
              </span>
            )}
          </div>
        </div>

        <div className="row px-3 mt-4">
          <div className="form-group mt-1 mb-1">
            <TextFieldStyled
              label="Miejsce urodzenia"
              variant="outlined"
              value={placeOfBirth}
              onChange={(event: any) => {
                handleSanitizedChange(
                  event.target.value,
                  setPlaceOfBirth,
                  "placeOfBirth",
                  setPlaceOfBirthInvalidChars
                );
              }}
              onBlur={(event: any) => {
                if (event.target.value)
                  setPlaceOfBirth(toTitleCase(event.target.value));
              }}
              inputProps={{
                maxLength: 200,
              }}
              InputProps={{
                style: { height: 50 },
              }}
            />
            {placeOfBirthInvalidChars && (
              <span className="validationError" role="alert">
                <i className="fa fa-exclamation-circle" />
                {placeOfBirthInvalidChars}
              </span>
            )}
          </div>
        </div>

        <div className="row px-3 mt-4">
          <div className="form-group mt-1 mb-1">
            <Autocomplete
              options={
                dictionary?.countries
                  .sort(compareDictionary)
                  .reduce(polandFirst, []) ?? []
              }
              value={countryOfBirth}
              onChange={(event: any, newValue: DictionaryCombo | null) => {
                setCountryOfBirth(newValue);
              }}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextFieldStyled
                  {...params}
                  label="Kraj urodzenia"
                  variant="outlined"
                  size="small"
                />
              )}
              noOptionsText={"Brak danych"}
            />
          </div>
        </div>

        <div className="row px-3 mt-4">
          <div className="form-group mt-1 mb-1">
            <Autocomplete
              options={dictionary?.identityDocumentTypes ?? []}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={documentType}
              onChange={(event: any, newValue: DictionaryCombo | null) => {
                setDocumentType(newValue);
              }}
              renderInput={(params) => (
                <TextFieldStyled
                  {...params}
                  label="Rodzaj dokumentu"
                  variant="outlined"
                  size="small"
                />
              )}
              noOptionsText={"Brak danych"}
            />
          </div>
        </div>

        <div className="row px-3 mt-4">
          <div className="form-group mt-1 mb-1">
            <TextFieldStyled
              label="Seria i numer"
              variant="outlined"
              name={`${getNumber()}Document`}
              value={seriesAndNumber}
              onChange={(event: any) => {
                setSeriesAndNumber(event.target.value);
              }}
              onBlur={(event: any) => {
                if (event.target.value)
                  setSeriesAndNumber(capitalize(event.target.value));
              }}
              InputProps={{
                style: { height: 50 },
              }}
              inputProps={{
                maxLength: 15,
              }}
            />
          </div>
          {seriesAndNumberRequiredError ? (
            <span className="validationError" role="alert">
              <i className="fa fa-exclamation-circle" />
              To pole jest wymagane
            </span>
          ) : null}
          {seriesAndNumberError ? (
            <span className="validationError" role="alert">
              <i className="fa fa-exclamation-circle" />
              Nieprawidłowa seria i numer
            </span>
          ) : null}
        </div>

        <div className="row px-3 mt-4">
          <div className="form-group mt-1 mb-1">
            <Autocomplete
              options={dictionary?.countries.reduce(polandFirst, []) ?? []}
              value={issuingCountry}
              onChange={(event: any, newValue: DictionaryCombo | null) => {
                setIssuingCountry(newValue);
              }}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextFieldStyled
                  {...params}
                  label="Kraj wydania"
                  variant="outlined"
                  size="small"
                />
              )}
              noOptionsText={"Brak danych"}
            />
          </div>
          {issuingCountryRequiredError ? (
            <span className="validationError" role="alert">
              <i className="fa fa-exclamation-circle" />
              To pole jest wymagane
            </span>
          ) : null}
        </div>

        <div className="row px-3 mt-4">
          <div className="form-group mt-2 mb-1">
            <Autocomplete
              options={dictionary?.countries.reduce(polandFirst, []) ?? []}
              value={country}
              onChange={(event: any, newValue: DictionaryCombo | null) => {
                setCountry(newValue);
                setCheckAddressPostalCode(
                  newValue?.name?.toLocaleLowerCase() === "polska"
                );
                setPostCode(undefined);
              }}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              renderInput={(params) => (
                <TextFieldStyled
                  {...params}
                  label="Kraj zamieszkania"
                  variant="outlined"
                  size="small"
                />
              )}
              noOptionsText={"Brak danych"}
            />
          </div>
        </div>

        <div className="row px-3  mt-4">
          <div className="form-group mt-1 mb-1">
            {checkAddressPostalCode ? (
              <TextFieldStyled
                label="Kod pocztowy"
                variant="outlined"
                inputProps={{
                  maxLength: 9,
                }}
                InputProps={{
                  style: { height: 50 },
                  inputProps: {
                    name: "address.postCode",
                    value: postCode?.substring(0, 6) ?? "",
                    onChange: (e: any) =>
                      e.target.value != postCode
                        ? setPostCode(e.target.value?.substring(0, 6))
                        : null,
                    disableUnderline: true,
                    maskValue: "00-000",
                  },
                  inputComponent: TextMaskCustom as any,
                }}
              />
            ) : (
              <TextFieldStyled
                value={postCode}
                onChange={(event: any) => {
                  setPostCode(event.target.value);
                }}
                label="Kod pocztowy"
                variant="outlined"
                inputProps={{
                  maxLength: 10,
                }}
                InputProps={{
                  style: { height: 50 },
                }}
              />
            )}
          </div>
          {postCodeError ? (
            <span className="validationError" role="alert">
              <i className="fa fa-exclamation-circle" />
              Nieprawidłowy kod pocztowy
            </span>
          ) : null}
        </div>

        <div className="row px-3 mt-4">
          <div className="form-group mt-1 mb-1">
            <TextFieldStyled
              label="Miejscowość"
              variant="outlined"
              value={city}
              onChange={(event: any) => {
                handleSanitizedChange(
                  event.target.value,
                  setCity,
                  "city",
                  setCityInvalidChars
                );
              }}
              onBlur={(event: any) => {
                if (event.target.value)
                  setCity(toTitleCase(event.target.value));
              }}
              inputProps={{
                maxLength: 200,
              }}
              InputProps={{
                style: { height: 50 },
              }}
            />
            {cityInvalidChars && (
              <span className="validationError" role="alert">
                <i className="fa fa-exclamation-circle" />
                {cityInvalidChars}
              </span>
            )}
          </div>
        </div>

        <div className="row px-3 mt-4">
          <div className="form-group mt-1 mb-1">
            <TextFieldStyled
              label="Ulica"
              variant="outlined"
              value={street}
              onChange={(event: any) => {
                handleSanitizedChange(
                  event.target.value,
                  setStreet,
                  "street",
                  setStreetInvalidChars
                );
              }}
              onBlur={(event: any) => {
                if (event.target.value)
                  setStreet(toTitleCase(event.target.value));
              }}
              inputProps={{
                maxLength: 200,
              }}
              InputProps={{
                style: { height: 50 },
              }}
            />
            {streetInvalidChars && (
              <span className="validationError" role="alert">
                <i className="fa fa-exclamation-circle" />
                {streetInvalidChars}
              </span>
            )}
          </div>
        </div>

        <div className="row px-3 mt-4">
          <div className="form-group mt-1 mb-1">
            <TextFieldStyled
              label="Numer domu"
              variant="outlined"
              value={house}
              onChange={(event: any) => {
                setHouse(event.target.value);
              }}
              inputProps={{
                maxLength: 10,
              }}
              InputProps={{
                style: { height: 50 },
              }}
            />
          </div>
        </div>

        <div className="row px-3 mt-4">
          <div className="form-group mt-1 mb-2">
            <TextFieldStyled
              label="Numer mieszkania"
              variant="outlined"
              value={apartment}
              onChange={(event: any) => {
                setApartment(event.target.value);
              }}
              inputProps={{
                maxLength: 10,
              }}
              InputProps={{
                style: { height: 50 },
              }}
            />
          </div>
        </div>

        <hr className="hrMargin" />

        <p className="bold red section">
          Czy beneficjent rzeczywisty {number} jest?
        </p>

        <div className="row px-3 mt-2" id={`${getNumber()}Option`}>
          <div
            onClick={() => {
              setOption(1);
              if (option !== 1) setPppText("");
              setCheckOption(true);
            }}
            className="custom-control custom-checkbox"
          >
            <input
              type="checkbox"
              className="custom-control-input"
              checked={option === 1}
            />
            <label className="custom-control-label">
              osobą zajmującą eksponowane stanowisko polityczne (PEP):
            </label>
          </div>
        </div>

        {option === 1 ? (
          <div className="row px-3 mt-3 mb-3">
            <div className="form-group mt-1 mb-1">
              <span className="pepLabel"></span>
              <TextFieldStyled
                label="Zajmowane stanowisko"
                variant="outlined"
                value={pppText}
                onChange={(event: any) => {
                  setPppText(event.target.value);
                }}
                onBlur={(event: any) => {
                  if (event.target.value)
                    setPppText(toTitleCase(event.target.value));
                }}
                error={optionNameError}
                inputProps={{
                  maxLength: 200,
                }}
                InputLabelProps={{
                  style: {
                    fontSize: 13,
                    whiteSpace: "normal",
                    wordWrap: "break-word",
                  },
                }}
                InputProps={{
                  style: {
                    height: 50,
                    fontSize: "12px !important",
                  },
                }}
              />
            </div>
            {optionNameError ? (
              <span className="validationError" role="alert">
                <i className="fa fa-exclamation-circle" />
                To pole jest wymagane
              </span>
            ) : null}
          </div>
        ) : null}

        <div className="row px-3">
          <div
            onClick={() => {
              setOption(2);
              if (option !== 2) setPppText("");
              setCheckOption(true);
            }}
            className="custom-control custom-checkbox"
          >
            <input
              type="checkbox"
              className="custom-control-input"
              checked={option === 2}
            />
            <label className="custom-control-label">
              członkiem rodziny osoby zajmującej eksponowane stanowisko
              polityczne:{" "}
            </label>
          </div>
        </div>

        {option === 2 ? (
          <div className="row px-3 mt-3 mb-3">
            <div className="form-group mt-1 mb-1">
              <span className="pepLabel"></span>
              <TextFieldStyled
                label="Nazwisko i imię osoby zajmującej eksponowane stanowisko polityczne, stanowisko osoby, relacja Klienta z PEP-em"
                variant="outlined"
                value={pppText}
                onChange={(event: any) => {
                  setPppText(event.target.value);
                }}
                onBlur={(event: any) => {
                  if (event.target.value)
                    setPppText(toTitleCase(event.target.value));
                }}
                error={optionNameError}
                inputProps={{
                  maxLength: 200,
                }}
                InputLabelProps={{
                  style: {
                    fontSize: 13,
                    whiteSpace: "normal",
                    wordWrap: "break-word",
                  },
                }}
                InputProps={{
                  style: { height: 50 },
                }}
              />
            </div>
            {optionNameError ? (
              <span className="validationError" role="alert">
                <i className="fa fa-exclamation-circle" />
                To pole jest wymagane
              </span>
            ) : null}
          </div>
        ) : null}

        <div className="row px-3">
          <div
            onClick={() => {
              setOption(3);
              if (option !== 3) setPppText("");
              setCheckOption(true);
            }}
            className="custom-control custom-checkbox"
          >
            <input
              type="checkbox"
              className="custom-control-input"
              checked={option === 3}
            />
            <label className="custom-control-label">
              osobą znaną jako bliski współpracownik osoby zajmującej
              eksponowane stanowisko polityczne:
            </label>
          </div>
        </div>

        {option === 3 ? (
          <div className="row px-3 mt-3 mb-3">
            <div className="form-group mt-1 mb-1">
              <span className="pepLabel"></span>
              <TextFieldStyled
                label="Nazwisko i imię osoby zajmującej eksponowane stanowisko polityczne, stanowisko osoby"
                variant="outlined"
                value={pppText}
                onChange={(event: any) => {
                  setPppText(event.target.value);
                }}
                onBlur={(event: any) => {
                  if (event.target.value)
                    setPppText(toTitleCase(event.target.value));
                }}
                error={optionNameError}
                inputProps={{
                  maxLength: 200,
                }}
                InputLabelProps={{
                  style: {
                    fontSize: 13,
                    whiteSpace: "normal",
                    wordWrap: "break-word",
                  },
                }}
                InputProps={{
                  style: { height: 50 },
                }}
              />
            </div>
            {optionNameError ? (
              <span className="validationError" role="alert">
                <i className="fa fa-exclamation-circle" />
                To pole jest wymagane
              </span>
            ) : null}
          </div>
        ) : null}

        <div className="row px-3">
          <div
            onClick={() => {
              setOption(0);
              setPppText("");
              setCheckOption(true);
            }}
            className="custom-control custom-checkbox"
          >
            <input
              type="checkbox"
              className="custom-control-input"
              checked={option === 0}
            />
            <label className="custom-control-label">
              nie jest osobą zajmującą eksponowane stanowisko polityczne,
              członkiem rodziny osoby zajmującej eksponowane stanowisko
              polityczne ani osobą znaną jako bliski współpracownik osoby
              zajmującej eksponowane stanowisko polityczne.{" "}
            </label>
          </div>
          {optionError ? (
            <span className="validationError" role="alert">
              <i className="fa fa-exclamation-circle" />
              To pole jest wymagane
            </span>
          ) : null}
        </div>

        {show ? (
          <div className="row px-3 mt-2">
            <button
              onClick={() => {
                if (setShow) setShow(true);
              }}
              className="addbeneficiary"
              type="submit"
            >
              Dodaj kolejnego beneficjenta rzeczywistego
            </button>
          </div>
        ) : null}
      </ThemeProvider>
    </div>
  );
};

export { BeneficiaryItem };
